import CoreBase from '../../../services/engines/CoreBase';

const COLLECTION_WRAPPER_TYPES = new Set([
    'IEnumerable', 'ICollection', 'IList', 'IReadOnlyCollection', 'IReadOnlyList',
    'List', 'Collection', 'ISet', 'HashSet', 'Queue', 'Stack', 'ObservableCollection',
]);

// Known BCL/framework types — excluded from "unresolved dependency" warnings
// (see isLikelyUserType below) since flagging every plain `string`/`int`
// parameter would be pure noise.
const KNOWN_BCL_TYPES = new Set([
    'string', 'int', 'bool', 'long', 'short', 'byte', 'char', 'double', 'float', 'decimal',
    'object', 'void', 'dynamic', 'var',
    'DateTime', 'DateTimeOffset', 'TimeSpan', 'Guid', 'Task', 'CancellationToken', 'Exception',
    'Type', 'Action', 'Func', 'EventHandler', 'Uri', 'Nullable',
    'IEnumerable', 'ICollection', 'IList', 'IReadOnlyCollection', 'IReadOnlyList',
    'List', 'Collection', 'ISet', 'HashSet', 'Queue', 'Stack', 'ObservableCollection',
    'Dictionary', 'IDictionary',
]);

/**
 * Resolves constructor-injected dependencies to the class/interface name they
 * should connect to on the canvas — pure data in, pure data out. Knows
 * nothing about shapes or connectors (that's ConnectorPainter/
 * AssociationConnectorBuilder's job).
 */
class DependencyResolver extends CoreBase {
    _name = 'DependencyResolver';

    /**
     * Reduces a parsed C# type string down to the bare class/interface name
     * it should resolve against. Strips nullable `?` and array brackets. If
     * the type is a known collection wrapper (IEnumerable<X>, List<X>, ...)
     * it unwraps to X (recursive, handles nested wrappers) — so
     * `IEnumerable<IEnvelopeTypeEventHandler>` resolves against
     * IEnvelopeTypeEventHandler, not "IEnumerable". Non-collection generics
     * (e.g. IRepository<Order>) keep resolving against the wrapper name.
     * @param {string} type
     * @returns {string|null}
     */
    resolveClassName = (type) => {
        if (!type) return null;

        const t = type.trim().replace(/\?$/, '').replace(/(\[\s*,*\s*\])+$/, '').replace(/\?$/, '');

        const genericIndex = t.indexOf('<');
        if (genericIndex === -1) return t.trim();

        const wrapper = t.slice(0, genericIndex).trim();
        if (!COLLECTION_WRAPPER_TYPES.has(wrapper)) return wrapper;

        const inner = t.slice(genericIndex + 1, t.lastIndexOf('>'));
        return this.resolveClassName(inner);
    };

    /**
     * Extracts every constructor-injected dependency across a set of
     * entries, resolved to a bare class/interface name. {id, name, members}
     * in, flat {fromId, toName}[] out.
     * @param {{id: string, name: string, members: object[]}[]} entries
     * @returns {{fromId: string, toName: string}[]}
     */
    resolveDependencies = (entries) => {
        const constructors = entries.flatMap((entry) =>
            (entry.members || [])
                .filter((member) => member.memberKind === 'constructor')
                .map((member) => ({ fromId: entry.id, deps: member.dependencies || [] })),
        );

        return constructors.flatMap(({ fromId, deps }) =>
            deps
                .map((dep) => ({ fromId, toName: this.resolveClassName(dep.type) }))
                .filter((d) => d.toName),
        );
    };

    /**
     * Heuristic: does this unresolved type name look like a user-defined
     * class/interface (PascalCase, not a known BCL/collection type) worth
     * warning about, vs a normal BCL parameter (string, int, ...) that would
     * just be noise?
     * @param {string} name
     * @returns {boolean}
     */
    isLikelyUserType = (name) => {
        if (!name) return false;
        return /^[A-Z]/.test(name) && !KNOWN_BCL_TYPES.has(name);
    };
}

const dependencyResolver = new DependencyResolver();
export default dependencyResolver;
